/**
 * Per-unit-kind data table.
 *
 * Values are derived from the decompiled original (see MECHANICS.md §9).
 * Initial HP per class is `(not located)` in the decomp — soldier/tank/cruiser
 * units survive multiple hits, while worker/truck die on first hit. We seed
 * defensible defaults below and gate them behind a single named constant so
 * tuning happens in one place.
 */

import { Difficulty, type Position, UnitKind } from "./types.js";

export interface UnitKindData {
  /** Mineral cost to produce at the factory. */
  readonly cost: number;
  /** Starting energy / displayable charge. The original reads this back on TAB. */
  readonly startingEnergy: number;
  /** Chebyshev sight/scan range for this unit's weapon/scan. */
  readonly sightRange: number;
  /** Hits-to-kill before the unit is removed. */
  readonly hitsToKill: number;
  /** Whether the unit moves on its own (false → it must be hand-placed and stays). */
  readonly mobile: boolean;
  /** Whether this unit can carry minerals from a deposit to the factory. */
  readonly canCarryMinerals: boolean;
  /** Whether this unit can destroy the enemy base on contact. */
  readonly canStrikeBase: boolean;
  /** Score multiplier for destroying the enemy base, before × difficulty. */
  readonly baseStrikeReward: number;
  /** Score for killing one of this unit type, before adding 50 × difficulty. */
  readonly killValueBase: number;
}

/**
 * Per-kind data. See MECHANICS.md §9.1 for the cost/energy/range columns and
 * §10.6 / help.txt for base-strike rewards. `hitsToKill` is engine-set
 * (decomp source not located).
 */
export const UNIT_DATA: Readonly<Record<UnitKind, UnitKindData>> = {
  [UnitKind.Worker]: {
    cost: 5,
    startingEnergy: 100,
    sightRange: 0,
    hitsToKill: 1,
    mobile: true,
    canCarryMinerals: false,
    canStrikeBase: false,
    baseStrikeReward: 0,
    killValueBase: 0,
  },
  [UnitKind.Truck]: {
    cost: 10,
    startingEnergy: 100,
    sightRange: 0,
    hitsToKill: 1,
    mobile: true,
    canCarryMinerals: true,
    canStrikeBase: false,
    baseStrikeReward: 0,
    killValueBase: 0,
  },
  [UnitKind.Soldier]: {
    cost: 20,
    startingEnergy: 300,
    sightRange: 4,
    hitsToKill: 3,
    mobile: true,
    canCarryMinerals: false,
    canStrikeBase: true,
    baseStrikeReward: 5_000,
    killValueBase: 500,
  },
  [UnitKind.ElectronicGun]: {
    cost: 30,
    startingEnergy: 400,
    sightRange: 5,
    hitsToKill: 4,
    mobile: false,
    canCarryMinerals: false,
    canStrikeBase: false,
    baseStrikeReward: 0,
    killValueBase: 1_000,
  },
  [UnitKind.RocketPlant]: {
    cost: 40,
    startingEnergy: 400,
    sightRange: 7,
    hitsToKill: 4,
    mobile: false,
    canCarryMinerals: false,
    canStrikeBase: false,
    baseStrikeReward: 0,
    killValueBase: 1_000,
  },
  [UnitKind.SpaceTank]: {
    cost: 50,
    startingEnergy: 600,
    sightRange: 5,
    hitsToKill: 5,
    mobile: true,
    canCarryMinerals: false,
    canStrikeBase: true,
    baseStrikeReward: 7_000,
    killValueBase: 1_500,
  },
  [UnitKind.SpaceScout]: {
    cost: 60,
    startingEnergy: 600,
    sightRange: 10,
    hitsToKill: 4,
    mobile: true,
    canCarryMinerals: false,
    canStrikeBase: false,
    baseStrikeReward: 0,
    killValueBase: 2_000,
  },
  [UnitKind.SpaceCruiser]: {
    cost: 70,
    startingEnergy: 700,
    sightRange: 6,
    hitsToKill: 6,
    mobile: true,
    canCarryMinerals: false,
    canStrikeBase: true,
    baseStrikeReward: 10_000,
    killValueBase: 2_500,
  },
} as const;

/** Hard slot cap per kind, applied in addition to the difficulty cap. */
export const HARD_SLOT_CAP = 10;

/** Maximum minerals the factory can hold. */
export const FACTORY_MINERAL_CAP = 100;

/** Minerals carried per truck load. */
export const TRUCK_LOAD = 10;

/**
 * Per-difficulty production cap for combat units (classes 3..8).
 * Workers and trucks (1, 2) ignore this cap — they only respect the hard slot cap.
 *
 * Source: MECHANICS.md §9 / factory produce-handlers FUN_004188b0..00418b80.
 */
export function unitCapForDifficulty(d: Difficulty): number {
  if (d === Difficulty.Easy) return HARD_SLOT_CAP;
  if (d === Difficulty.Online) return 5;
  return 2 * d;
}

/**
 * Starting mineral pool for the player.
 * `(6 - difficulty) * 20` per FUN_00407900, with online forced to 100.
 */
export function startingMinerals(d: Difficulty): number {
  if (d === Difficulty.Online) return 100;
  return (6 - d) * 20;
}

/**
 * Reaction window in ticks for the player's "beside the mark" timing.
 * `8 - difficulty` ticks per MECHANICS.md §4.
 */
export function reactionWindow(d: Difficulty): number {
  return 8 - d;
}

/**
 * Energetic-shield budget: ticks of shielding granted on the `S` key.
 * `8 - difficulty` per FUN_00411d90.
 */
export function shieldBudget(d: Difficulty): number {
  return 8 - d;
}

/**
 * Score awarded for destroying an enemy unit of `kind` at `difficulty`.
 * `killValueBase + 50 * difficulty` per help.txt / MECHANICS.md §6.
 */
export function killScore(kind: UnitKind, difficulty: Difficulty): number {
  return UNIT_DATA[kind].killValueBase + 50 * difficulty;
}

/**
 * Score awarded for striking the enemy base with `kind`.
 * `baseStrikeReward * difficulty` per MECHANICS.md §10.6.
 * Returns 0 for units that can't strike the base.
 */
export function baseStrikeScore(kind: UnitKind, difficulty: Difficulty): number {
  return UNIT_DATA[kind].baseStrikeReward * difficulty;
}

/** Shield orientation for movers (soldier/tank/cruiser). Statics just toggle on/off. */
export type ShieldPosition = "left" | "center" | "right";

/** Where on the L/R sweep arc the target radar pointer currently sits. */
export type RadarPointer = "left" | "center" | "right";

/**
 * Per-mover movement intent. The Game tick() advances the unit one cell per
 * tick when `moving` is true; if `cellsRemaining > 0` it decrements and stops
 * automatically at zero.
 */
export interface MoveIntent {
  moving: boolean;
  /** -1 = move indefinitely; 0 or positive = remaining cells to step. */
  cellsRemaining: number;
}

/** Truck job-state — autonomous transport pipeline. */
export type TruckJob = "idle" | "going_to_worker" | "going_to_factory";

/** Mutable unit instance — what lives in the game's grid. */
export interface Unit {
  readonly id: number;
  readonly kind: UnitKind;
  readonly owner: 0 | 1;
  position: Position;
  direction: number;
  energy: number;
  hp: number;
  alive: boolean;

  /** Ticks of shielding still active (0 = no shield). */
  shieldTicks: number;
  /** Where the mover is holding their shield this tick. */
  shieldPosition: ShieldPosition;

  /** Player movement state for movers. */
  move: MoveIntent;

  /** Search-radar passive scan toggled by R. */
  searchRadarOn: boolean;
  /** Unit id this unit has currently locked on, or null. */
  targetUnitId: number | null;
  /** Where the player's target-radar pointer is relative to the locked enemy. */
  targetPointer: RadarPointer;

  /** Weapon menu currently open? */
  weaponMenuOpen: boolean;
  /** Currently-selected weapon index (kind-specific). null = none chosen. */
  weaponSelected: number | null;

  /** True iff a static unit has been placed (gun/plant). */
  placed: boolean;

  /** Minerals on hand (workers cap at 10). */
  mineralsCarried: number;
  /** Truck pipeline state. */
  truckJob: TruckJob;
  /** Worker id this truck is currently servicing, or null. */
  truckWorkerId: number | null;
}

/**
 * Create a fresh unit at full health and starting energy for its kind.
 * Sets sensible defaults; the caller is responsible for placement.
 */
export function createUnit(args: {
  id: number;
  kind: UnitKind;
  owner: 0 | 1;
  position: Position;
  direction?: number;
}): Unit {
  const data = UNIT_DATA[args.kind];
  return {
    id: args.id,
    kind: args.kind,
    owner: args.owner,
    position: args.position,
    direction: args.direction ?? 1,
    energy: data.startingEnergy,
    hp: data.hitsToKill,
    alive: true,

    shieldTicks: 0,
    shieldPosition: "center",

    move: { moving: false, cellsRemaining: 0 },

    searchRadarOn: false,
    targetUnitId: null,
    targetPointer: "center",

    weaponMenuOpen: false,
    weaponSelected: null,

    placed: data.mobile,
    mineralsCarried: 0,
    truckJob: "idle",
    truckWorkerId: null,
  };
}

